<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

// Days of the week kept in the same order the backend permits them.
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// A curated list of timezones with the Brazilian ones first, since the product
// is pt-BR first. Values are IANA identifiers understood by ActiveSupport.
const TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'América/São Paulo (GMT-3)' },
  { value: 'America/Fortaleza', label: 'América/Fortaleza (GMT-3)' },
  { value: 'America/Recife', label: 'América/Recife (GMT-3)' },
  { value: 'America/Bahia', label: 'América/Salvador (GMT-3)' },
  { value: 'America/Belem', label: 'América/Belém (GMT-3)' },
  { value: 'America/Manaus', label: 'América/Manaus (GMT-4)' },
  { value: 'America/Cuiaba', label: 'América/Cuiabá (GMT-4)' },
  { value: 'America/Porto_Velho', label: 'América/Porto Velho (GMT-4)' },
  { value: 'America/Rio_Branco', label: 'América/Rio Branco (GMT-5)' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
  { value: 'UTC', label: 'UTC (GMT+0)' },
  { value: 'America/New_York', label: 'America/New York (GMT-5)' },
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon (GMT+0)' },
];

const buildBusinessHours = (config = {}) => {
  const stored = config.company_business_hours || {};
  return WEEKDAYS.reduce((acc, day) => {
    const value = stored[day] || {};
    acc[day] = {
      enabled: value.enabled || false,
      openTime: value.open_time || '08:00',
      closeTime: value.close_time || '18:00',
    };
    return acc;
  }, {});
};

const state = reactive({
  productName: '',
  companyDescription: '',
  timezone: 'America/Sao_Paulo',
  businessHours: buildBusinessHours(),
});

const validationRules = {
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field =>
  v$.value[field].$error ? v$.value[field].$errors[0].$message : '';

const formErrors = computed(() => ({
  productName: getErrorMessage('productName'),
}));

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.productName = config.product_name;
  state.companyDescription = config.company_description || '';
  state.timezone = config.timezone || 'America/Sao_Paulo';
  state.businessHours = buildBusinessHours(config);
};

const serializeBusinessHours = () =>
  WEEKDAYS.reduce((acc, day) => {
    const value = state.businessHours[day];
    acc[day] = {
      enabled: value.enabled,
      open_time: value.enabled ? value.openTime : '',
      close_time: value.enabled ? value.closeTime : '',
    };
    return acc;
  }, {});

const handleSubmit = async () => {
  const result = await v$.value.productName.$validate();
  if (!result) return;

  emit('submit', {
    config: {
      product_name: state.productName,
      company_description: state.companyDescription,
      timezone: state.timezone,
      company_business_hours: serializeBusinessHours(),
    },
  });
};

defineExpose({ submit: handleSubmit });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.productName"
      :label="t('CAPTAIN.ASSISTANTS.FORM.COMPANY_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.COMPANY_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
    />

    <Editor
      v-model="state.companyDescription"
      :label="t('CAPTAIN.ASSISTANTS.FORM.COMPANY_DESCRIPTION.LABEL')"
      :placeholder="
        t('CAPTAIN.ASSISTANTS.FORM.COMPANY_DESCRIPTION.PLACEHOLDER')
      "
      :max-length="500"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TIMEZONE.LABEL') }}
      </label>
      <Select v-model="state.timezone" :options="TIMEZONE_OPTIONS" />
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.LABEL') }}
      </label>
      <div v-for="day in WEEKDAYS" :key="day" class="flex items-center gap-3">
        <div class="flex items-center w-40 gap-2">
          <Switch v-model="state.businessHours[day].enabled" />
          <span class="text-sm text-n-slate-12">
            {{ t(`CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.DAYS.${day}`) }}
          </span>
        </div>
        <template v-if="state.businessHours[day].enabled">
          <input
            v-model="state.businessHours[day].openTime"
            type="time"
            class="px-2 py-1 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12"
          />
          <span class="text-n-slate-11">{{
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.TO')
          }}</span>
          <input
            v-model="state.businessHours[day].closeTime"
            type="time"
            class="px-2 py-1 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12"
          />
        </template>
        <span v-else class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.CLOSED') }}
        </span>
      </div>
    </div>
  </div>
</template>
